import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import * as _ from 'lodash';
import * as jstz from 'jstimezonedetect';
import { UiService } from '../../../../services/core.ui.service';
import { GfTranslateService } from '../../../../services/core.translate.service';
import { UserRest } from '../../../../services/core.user.rest.service';
import { UserService } from '../../../../services/core.user.service';
import { StateService } from '@uirouter/angular';
import { StateHelperService } from '../../../../services/core.ui.state.service';
@Component({
  selector: 'register-view',
  template: require('./register.component.html')
})
export class RegisterViewComponent implements OnInit, OnDestroy {
  @Input() siteConfig: any;

  userModel: any = {
    template: {
      unixName: '',
      password: '',
      email: '',
      firstname: '',
      lastname: '',
      timezone: 'UTC',
      confirmationHash: '',
      isGroup: 'f',
      registrationToken: ''
    },
    errorTypes: {
      unixName_system: 'Unix name is reserved.',
      unixName_unallowed: 'Unix name is not allowed.',
      unixName_database: 'Unix name already registered.',
      email_database: 'Email already registered.',
      length: 'Bad length.',
      required: 'Required field.',
      Invalid: 'Use lowercase letters, numbers or underscore.',
      Numeric: 'Numeric names not allowed.',
      exists: 'Already registered.',
      'Not Found': 'User not found.',
      changed: 'Updating unix name is restricted.'
    },
    data: {},
    url: '/api/userregister'
  };

  checkingUserName: boolean = false;
  userNameUnique: boolean = true;
  registering: boolean = false;
  showEmailBackendErrors: boolean = false;

  captchaRefresh: number;
  registrationMode: number;
  useSSO: boolean;
  SSORequirePassword: string;
  requireCaptcha: boolean;
  usernameRulesDesc: any;
  passwordRulesDesc: any;

  emailExists: boolean;
  emailInvalid: boolean;
  activeTab: string;
  passwordMismatch: boolean;
  tokenErrorMessage: string;
  emailBackendErrors: string;

  private pendingUsernameCheck: any = null;

  constructor(
    private stateService: StateService, 
    
    private userRest: UserRest,
    private uiService: UiService,
    private translateService: GfTranslateService,
    private userService: UserService,
    private stateHelperService: StateHelperService
  ) {}

  ngOnInit() {
    this.captchaRefresh = new Date().getTime();
    this.initUserModel();

    // Initialize userModel data from route parameters.
    this.userModel.data = {
      unixName: _.get(this.stateService.params, 'unixName', ''),
      email: decodeURIComponent(_.get(this.stateService.params, 'email', '')),
      password: _.get(this.stateService.params, 'password', ''),
      token: _.get(this.stateService.params, 'token', ''),
      captcha: ''
    };
    this.userModel.data.registrationToken = _.get(this.stateService.params, 'token', '');

    // Configure settings based on site configuration.
    this.registrationMode = _.get(this.siteConfig, 'registrationMode', 0);
    this.useSSO = _.get(this.siteConfig, 'authenticationMode', 'db') === 'sso';
    this.SSORequirePassword = _.get(this.siteConfig, 'SSORequirePassword', 'N');
    this.requireCaptcha = Number(_.get(this.siteConfig, 'requireUserCaptcha', 0)) && !!this.stateService.params['invite'];
    this.usernameRulesDesc = _.get(this.siteConfig, 'usernameRulesDesc', 0);
    this.passwordRulesDesc = _.get(this.siteConfig, 'passwordRulesDesc', false);

    this.uiService.setFocus('registerUnixName');

    if (this.pendingUsernameCheck) {
      clearTimeout(this.pendingUsernameCheck);
    }

    if (this.useSSO) {
      this.userService.getSSOID().then((response: any) => {
        this.userModel.data.unixName = response.data;
      });
    }

    if (this.hasUnixName()) {
      this.checkUniqueName();
    }
  }

  ngOnDestroy() {
    if (this.pendingUsernameCheck) {
      clearTimeout(this.pendingUsernameCheck);
    }
  }

  private initUserModel(): void {
    this.userModel.data = _.cloneDeep(this.userModel.template);
    this.resetUserModelErrors();
  }

  private resetUserModelErrors(): void {
    this.userModel.errors = _.cloneDeep(this.userModel.template);
  }

  scheduleUniqueCheck() {
    if (this.hasUnixName()) {
      this.clearFieldErrors('unixName');
      this.userNameUnique = true;
      this.checkingUserName = true;
      if (this.pendingUsernameCheck) {
        clearTimeout(this.pendingUsernameCheck);
      }
      this.pendingUsernameCheck = setTimeout(() => this.checkUniqueName(), 1000);
    }
  }

  registerUser() {
    this.resetUserModelErrors();

    // Set timezone using jstz library.
    const tz = jstz.determine();
    this.userModel.data.timezone = tz.name();

    if (this.useSSO && !this.SSORequirePassword) {
      const randomPass = Math.random().toString(36).slice(-8);
      this.userModel.data.password = randomPass;
      this.userModel.data.confirm_password = randomPass;
    }

    if (this.userModel.data.password !== this.userModel.data.confirm_password) {
      this.uiService.message.showError('Passwords Do Not Match');
      this.activeTab = 'Project';
      return;
    }

    this.registering = true;
    this.emailExists = false;
    this.emailInvalid = false;

    return this.userRest.register(this.userModel.data)
      .then((response: any) => {
        if (this.useSSO) {
          this.userService.session.recover(true).then((res: any) => {
            if (this.stateService.params.url) {
              if (!this.stateHelperService.navigateByUrl(this.stateService.params.url)) {
                this.stateService.go('home');
              }
              return;
            }
            const next = this.userService.options.getLogonDestinationState();
            this.stateService.go(next.name, next.params, { reload: true });
          });
        } else {
          if (response?.status == 1) {
            this.uiService.message.showSuccess(
              this.translateService.translate('Your user was registered and has been approved, you can login now using your credentials'),
              this.translateService.translate('User registration submitted')
            );
            this.stateService.go('home.user.login', { unixName: this.userModel.data.unixName });
          } else {
            this.uiService.message.showSuccess(
              this.translateService.translate('Your user was registered and is pending approval, you will receive an approval confirmation when your user is approved.'),
              this.translateService.translate('User registration submitted')
            );
            this.stateService.go('home');
          }
        }
      }, (response: any) => this.handleRegistrationFailure(response))
      .finally(() => {
        this.registering = false;
      });
  }

  validatePassword() {
    this.clearFieldErrors('password');
    this.passwordMismatch = this.userModel.data.password !== this.userModel.data.confirm_password;
  }

  validateEmail() {
    this.clearFieldErrors('email');
    this.showEmailBackendErrors = false;
    this.emailBackendErrors = '';
  }

  hasUnixName(): boolean {
    return _.size(_.get(this, 'userModel.data.unixName', '')) > 2;
  }

  hasFieldErrors(fieldName: string): boolean {
    return this.getFieldErrors(fieldName)?.length > 0;
  }

  hasFieldErrorType(fieldName: string, errorType: string): boolean {
    return _.filter(this.getFieldErrors(fieldName), { type: errorType }).length > 0;
  }

  getFieldErrors(fieldName: string): any[] {
    return _.get(this.userModel, 'errors.' + fieldName, []);
  }

  private clearFieldErrors(fieldName: string) {
    _.set(this.userModel, 'errors.' + fieldName, []);
  }

  private handleRegistrationFailure(response: any) {
    this.userModel.errors = _.get(response, 'error', {});

    if (_.get(response, 'type', '') === 'user-registration-token-not-exist') {
      this.tokenErrorMessage = this.translateService.translate('The token for this email does not exist. You need to register your email and get a valid token before you can register your account.');
    }

    this.emailExists = this.hasFieldErrorType('email', 'email_db_register');
    this.emailInvalid = (!this.emailExists) && this.hasFieldErrors('email');

    if (this.hasFieldErrors('captcha')) {
      this.captchaRefresh = new Date().getTime();
      this.uiService.message.showError(
        this.translateService.translate('The CAPTCHA you entered was incorrect'),
        this.translateService.translate('Register User')
      );
      this.userModel.data.captcha = '';
    }
  }

  private checkUniqueName() {
    this.checkingUserName = true;
    const unixNameLower = (_.get(this, 'userModel.data.unixName', '')).toLowerCase();
    this.userRest.getBasic(unixNameLower)
      .then((res: any) => {
        // A 200 response means the username exists.
        this.setUniqueState(false);
      }, (res: any) => {
        // 404 Not Found means the username is available;
        // 400 or 403 indicate invalid/blacklisted username.
        this.setUniqueState(res.status === 404);
      });
  }

  private setUniqueState(isValid: boolean) {
    this.checkingUserName = false;
    this.userNameUnique = isValid;
    this.pendingUsernameCheck = false;
  }
}
